import React, { useEffect, useState } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import "./UserIssues.css";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";

const UserIssues = () => {
  const { userId, loading } = useGetCurrentUserId();
  const [issues, setIssues] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    const db = getDatabase();
    const issuesRef = ref(db, "transport_issues");

    const unsubscribe = onValue(issuesRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        setIssues([]);
        return;
      }

      let allIssues = [];

      Object.values(data).forEach((transporterIssues) => {
        if (!transporterIssues) return;

        Object.values(transporterIssues).forEach((item) => {
          if (
            item.clientId &&
            String(item.clientId).trim() === String(userId).trim()
          ) {
            allIssues.push(item);
          }
        });
      });

      // latest first
      allIssues.sort((a, b) => (b.reportedAt || 0) - (a.reportedAt || 0));

      setIssues(allIssues);
    });

    return () => unsubscribe();
  }, [userId]);

  if (loading) return <p className="loading">Loading...</p>;

  return (
    <div className="issue-container">
      <div className="issue-header">
        {/* LEFT: Back */}
        <button className="issue-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </button>

        {/* CENTER: Title */}
        <h1 className="issue-title">Reported Issues</h1>

        {/* RIGHT: Info */}
        <button className="issue-info-btn" onClick={() => setShowInfo(true)}>
          <Info size={20} />
        </button>
      </div>

      {showInfo && (
        <div className="issue-overlay">
          <div className="issue-modal">
            <button
              className="issue-close"
              onClick={() => setShowInfo(false)}
            >
              ✕
            </button>

            <h3>About This Page</h3>

            <ul>
              <li>View all reported transport issues</li>
              <li>Track truck-related problems</li>
              <li>See order and route details</li>
              <li>Monitor issue history</li>
            </ul>
          </div>
        </div>
      )}

      {issues.length === 0 ? (
        <p className="no-data">No issues reported yet.</p>
      ) : (
        <>
          {/* ✅ DESKTOP TABLE */}
          <div className="issue-table-wrapper">
            <table className="issue-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Truck</th>
                  <th>Driver No.</th>
                  <th>Load</th>
                  <th>Order</th>
                  <th>Route</th>
                  <th>Issue</th>
                  <th>Date</th>
                </tr>
              </thead>

              <tbody>
                {issues.map((item, index) => (
                  <tr key={item.issueId || index}>
                    <td>{index + 1}</td>
                    <td>{item.truckNumber || "-"}</td>
                    <td>{item.driverNumber || "-"}</td>
                    <td>
                      {item.load || "-"}
                      {item.unit ? ` ${item.unit}` : ""}
                    </td>
                    <td className="text-primary">{item.orderId || "-"}</td>
                    <td>{item.route || "-"}</td>
                    <td className="issue-text">{item.issue || "-"}</td>
                    <td>
                      {item.reportedAt
                        ? new Date(item.reportedAt).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ✅ MOBILE CARD VIEW */}
          <div className="issue-mobile">
            {issues.map((item, index) => (
              <div key={item.issueId || index} className="issue-card">
                <div className="issue-card-header">
                  <strong>Truck: {item.truckNumber || "-"}</strong>
                </div>

                <div className="issue-row">
                  <b className="text-secondary">Driver No:</b>
                  <span>{item.driverNumber || "-"}</span>
                </div>

                <div className="issue-row">
                  <b className="text-secondary">Load:</b>
                  <span>
                    {item.load || "-"}
                    {item.unit ? ` ${item.unit}` : ""}
                  </span>
                </div>

                <div className="issue-row">
                  <b className="text-secondary">Order Id:</b>
                  <span className="text-primary">{item.orderId || "-"}</span>
                </div>
                
                <div className="issue-row">
                  <b className="text-secondary">Route:</b>
                  <span>{item.route || "-"}</span>
                </div>

                <div className="issue-description">
                  <b className="text-secondary">Issue:</b> {item.issue || "-"}
                </div>

                <div className="issue-date">
                  {item.reportedAt
                    ? new Date(item.reportedAt).toLocaleString()
                    : "-"}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default UserIssues;